import React, { useState, useContext, useMemo, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import CollapseHeader from "../../../../core/common/collapse-header";
import { all_routes } from "../../../router/all_routes";
import { AuthContext } from "../../../../context/authcontext";
import SettingsTabsNavigation from "../../../../components/Tabs/SettingsTabsNavigation";
import {
  showSuccessAlert,
  showErrorAlert,
} from "../../../uiInterface/base-ui/alertservice";
import Swal from "sweetalert2";
import "./AutoCreationRules.css";
import {
  AutoCreationRulesService,
  CrmWorkflow,
  EntityField,
  RuleOperator,
  WorkflowCondition,
} from "./autoCreationRules.service";
import { getLifecycleTypeName } from "../../../../utils/lifecycleUtils";

interface ConditionItem {
  id: string;
  field: string;
  operator: string;
  value: string[];
  join: string;
  allowedOperators: string[];
}

const genId = () => Math.random().toString(36).substring(2, 11);

const AutoCreationRules: React.FC = () => {
  const route = all_routes;
  const { selectedTenantId, auth } = useContext(AuthContext);

  const currentUserRole = useMemo(() => {
    if (!auth?.tenants || !selectedTenantId) return null;
    const currentTenant = auth.tenants.find(
      (t: any) => String(t.tenant_id) === String(selectedTenantId)
    );
    return currentTenant?.role_key || null;
  }, [auth?.tenants, selectedTenantId]);

  const isReadOnly = currentUserRole === "SALESUSER";

  const [rules, setWorkflows] = useState<CrmWorkflow[]>([]);
  const [entityFields, setEntityFields] = useState<EntityField[]>([]);
  const [operatorOptions, setOperatorOptions] = useState<RuleOperator[]>([]);
  const [loading, setLoading] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState<CrmWorkflow | null>(null);
  const [saving, setSaving] = useState(false);

  const [configName, setConfigName] = useState("");
  const [isEnabled, setIsEnabled] = useState(true);
  const [conditions, setConditions] = useState<ConditionItem[]>([]);
  const [expandedConditionId, setExpandedConditionId] = useState<string | null>(null);
  const [createContact, setCreateContact] = useState(true);
  const [createLead, setCreateLead] = useState(true);

  useEffect(() => {
    if (!selectedTenantId) return;
    setLoading(true);
    Promise.all([
      AutoCreationRulesService.fetchWorkflows(selectedTenantId),
      AutoCreationRulesService.fetchEntityFields("mail-message"),
      AutoCreationRulesService.fetchRuleOperators(),
    ])
      .then(([wfs, fields, operators]) => {
        const filteredWfs = wfs.filter((wf: any) => 
           wf.workflow_type !== "lead_to_pipeline_on_first_email" && 
           wf.workflow_type !== "inactivity_based_on_email_activity"
        );
        setWorkflows(filteredWfs);
        setEntityFields(fields);
        setOperatorOptions(operators);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [selectedTenantId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.aw-card-dropdown') && !target.closest('.aw-card-menu-btn')) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getFilteredOperators = useCallback(
    (allowedOperators: string[]) => {
      if (allowedOperators.length === 0) return operatorOptions;
      return operatorOptions.filter(
        (op) =>
          allowedOperators.includes(op.approval_rule_operator_key.toUpperCase()) ||
          allowedOperators.includes(op.approval_rule_operator_key)
      );
    },
    [operatorOptions]
  );

  const getFieldDisplay = (fieldName: string) => {
    const field = entityFields.find((f) => f.field_name === fieldName);
    return field?.display_name || fieldName;
  };

  const getOperatorDisplay = (opKey: string) => {
    if (opKey === "WILDCARD") return "Wildcard";
    const op = operatorOptions.find(
      (o) => o.approval_rule_operator_key === opKey
    );
    if (op) return op.approval_rule_operator_display_name;
    return opKey;
  };

  const resetModal = () => {
    setConfigName("");
    setIsEnabled(true);
    setConditions([]);
    setExpandedConditionId(null);
    setCreateContact(true);
    setCreateLead(true);
    setEditingWorkflow(null);
  };

  const openCreateModal = () => {
    resetModal();
    setShowModal(true);
  };

  const openEditModal = (rule: CrmWorkflow) => {
    setEditingWorkflow(rule);
    setConfigName(rule.config_name);
    setIsEnabled(rule.is_enabled);

    const entities = rule.config_json?.target_entity_config?.create || [];
    setCreateContact(entities.includes("contact"));
    setCreateLead(entities.includes("lead"));

    const apiConditions = rule.config_json?.filter_config?.conditions || [];
    const mappedConditions: ConditionItem[] = apiConditions.map((c: WorkflowCondition, idx: number) => {
      const field = entityFields.find((f) => f.field_name === c.field);
      const allowed = field?.allowed_operators || [];
      return {
        id: genId(),
        field: c.field,
        operator: c.operator,
        value: Array.isArray(c.value) ? c.value : [c.value],
        join: idx === 0 ? "" : c.join || "AND",
        allowedOperators: allowed,
      };
    });
    setConditions(mappedConditions);
    setExpandedConditionId(null);
    setShowModal(true);
    setOpenMenuId(null);
  };

  const closeModal = () => {
    setShowModal(false);
    resetModal();
  };

  const addCondition = () => {
    const defaultField = entityFields.length > 0 ? entityFields[0].field_name : "";
    const defaultAllowed = entityFields.length > 0 ? entityFields[0].allowed_operators : [];
    const matchingOps = operatorOptions.filter(
      (op) =>
        defaultAllowed.includes(op.approval_rule_operator_key.toUpperCase()) ||
        defaultAllowed.includes(op.approval_rule_operator_key)
    );
    const defaultOp = matchingOps.length > 0 ? matchingOps[0].approval_rule_operator_key : "";

    const newCondition: ConditionItem = {
      id: genId(),
      field: defaultField,
      operator: defaultOp,
      value: [],
      join: conditions.length > 0 ? "AND" : "",
      allowedOperators: defaultAllowed,
    };
    setConditions((prev) => [...prev, newCondition]);
    setExpandedConditionId(newCondition.id);
  };

  const updateCondition = (id: string, updates: Partial<ConditionItem>) => {
    setConditions((prev) =>
      prev.map((c) => (c.id === id ? { ...c, ...updates } : c))
    );
  };

  const removeCondition = (id: string) => {
    setConditions((prev) => {
      const filtered = prev.filter((c) => c.id !== id);
      if (filtered.length > 0 && filtered[0].join) {
        filtered[0] = { ...filtered[0], join: "" };
      }
      return filtered;
    });
    if (expandedConditionId === id) setExpandedConditionId(null);
  };

  const handleFieldChange = (id: string, fieldName: string) => {
    const field = entityFields.find((f) => f.field_name === fieldName);
    const allowed = field?.allowed_operators || [];
    const matchingOps = operatorOptions.filter(
      (op) =>
        allowed.includes(op.approval_rule_operator_key.toUpperCase()) ||
        allowed.includes(op.approval_rule_operator_key)
    );
    const defaultOp = matchingOps.length > 0 ? matchingOps[0].approval_rule_operator_key : "";
    updateCondition(id, {
      field: fieldName,
      operator: defaultOp,
      value: [],
      allowedOperators: allowed,
    });
  };

  const handleJoinChange = (idx: number, joinKey: string) => {
    setConditions((prev) => {
      const newConditions = [...prev];
      if (joinKey) {
        for (let i = 1; i < newConditions.length; i++) {
          if (newConditions[i].join) {
            newConditions[i] = { ...newConditions[i], join: joinKey };
          }
        }
      }
      newConditions[idx] = { ...newConditions[idx], join: joinKey };
      return newConditions;
    });
  };

  const handleSave = async () => {
    if (!configName.trim()) {
      await showErrorAlert("Please enter a rule name.");
      return;
    }
    if (conditions.length === 0) {
      await showErrorAlert("Please add at least one condition.");
      return;
    }
    if (!createContact && !createLead) {
      await showErrorAlert(`Please select at least one entity to create (Contact or ${getLifecycleTypeName("LEAD")}).`);
      return;
    }

    const targetEntities: string[] = [];
    if (createContact) targetEntities.push("contact");
    if (createLead) targetEntities.push("lead");

    const apiConditions = conditions.map((c, idx) => {
      const base: any = {
        field: c.field,
        value: c.value.length === 1 ? c.value[0] : c.value,
        operator: c.operator,
      };
      if (idx > 0 && c.join) {
        base.join = c.join;
      }
      return base;
    });

    const payload = {
      config_name: configName.trim(),
      is_enabled: isEnabled,
      config_json: {
        filter_config: {
          conditions: apiConditions,
        },
        target_entity_config: {
          create: targetEntities,
        },
      },
      tenant_id: selectedTenantId!,
      created_by: auth?.user_id,
      updated_by: auth?.user_id,
    };

    setSaving(true);
    try {
      if (editingWorkflow) {
        const updated = await AutoCreationRulesService.updateWorkflow(
          editingWorkflow.crm_workflow_id,
          payload
        );
        setWorkflows((prev) =>
          prev.map((w) =>
            w.crm_workflow_id === editingWorkflow.crm_workflow_id ? updated : w
          )
        );
        await showSuccessAlert("Rule updated successfully.");
      } else {
        const created = await AutoCreationRulesService.createWorkflow(payload as any);
        setWorkflows((prev) => [...prev, created]);
        await showSuccessAlert("Rule created successfully.");
      }
      closeModal();
    } catch (err) {
      console.error(err);
      await showErrorAlert("Failed to save rule.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setOpenMenuId(null);
    const result = await Swal.fire({
      title: "Delete Rule?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });
    if (result.isConfirmed) {
      try {
        await AutoCreationRulesService.deleteWorkflow(id);
        setWorkflows((prev) => prev.filter((w) => w.crm_workflow_id !== id));
        await showSuccessAlert("Rule deleted successfully.");
      } catch (err) {
        console.error(err);
        await showErrorAlert("Failed to delete rule.");
      }
    }
  };

  const handleToggle = async (rule: CrmWorkflow) => {
    const actionText = rule.is_enabled ? "disable" : "enable";
    const result = await Swal.fire({
      title: `${actionText.charAt(0).toUpperCase() + actionText.slice(1)} Rule?`,
      text: `Are you sure you want to ${actionText} this rule?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#6366f1",
      cancelButtonColor: "#64748b",
      confirmButtonText: `Yes, ${actionText} it`,
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const updated = await AutoCreationRulesService.toggleWorkflow(
          rule.crm_workflow_id,
          !rule.is_enabled
        );
        setWorkflows((prev) =>
          prev.map((w) =>
            w.crm_workflow_id === rule.crm_workflow_id ? updated : w
          )
        );
        await showSuccessAlert(`Rule ${actionText}d successfully.`);
      } catch (err) {
        console.error(err);
        await showErrorAlert(`Failed to ${actionText} rule.`);
      }
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getUserInitials = (user: { first_name: string; last_name: string }) => {
    return `${user.first_name?.charAt(0) || ""}${user.last_name?.charAt(0) || ""}`.toUpperCase();
  };

  const renderConditionConnector = (index: number) => {
    if (index === 0) return null;
    const condition = conditions[index];
    const joinKey = condition.join;

    return (
      <div className="aw-rule-connector">
        <div className="aw-join-picker">
          <button
            className={`aw-join-picker-btn ${joinKey === "AND" ? "aw-join-picker-btn-active-and" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              if (!isReadOnly) handleJoinChange(index, joinKey === "AND" ? "" : "AND");
            }}
            disabled={isReadOnly}
            style={{ cursor: isReadOnly ? "default" : undefined }}
          >
            AND
          </button>
          <button
            className={`aw-join-picker-btn ${joinKey === "OR" ? "aw-join-picker-btn-active-or" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              if (!isReadOnly) handleJoinChange(index, joinKey === "OR" ? "" : "OR");
            }}
            disabled={isReadOnly}
            style={{ cursor: isReadOnly ? "default" : undefined }}
          >
            OR
          </button>
        </div>
      </div>
    );
  };

  const EMAIL_FIELDS = ["mail_cc", "send_from", "send_to"];

  const isEmailField = (fieldName: string) => EMAIL_FIELDS.includes(fieldName);

  const renderValueInput = (condition: ConditionItem) => {
    if (isEmailField(condition.field)) {
      return (
        <div className="aw-field-group">
          <label className="aw-field-label">Value</label>
          <div className="aw-value-tags-container">
            {condition.value.map((v, idx) => (
              <span key={idx} className="aw-value-tag">
                {v}
                {!isReadOnly && (
                  <i
                    className="ti ti-x"
                    onClick={() => {
                      const newValues = condition.value.filter((_, i) => i !== idx);
                      updateCondition(condition.id, { value: newValues });
                    }}
                  />
                )}
              </span>
            ))}
            {!isReadOnly && (
              <input
                className="aw-value-tags-input"
                type="email"
                placeholder={condition.value.length > 0 ? "Add email..." : "Enter email..."}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    let val = e.currentTarget.value.trim();
                    if (val) {
                      if (condition.operator === "WILDCARD" && !val.startsWith("*")) {
                        val = "*" + val;
                      }
                      if (!condition.value.includes(val)) {
                        updateCondition(condition.id, {
                          value: [...condition.value, val],
                        });
                      }
                    }
                    e.currentTarget.value = "";
                  }
                }}
              />
            )}
          </div>
          {!isReadOnly && <span className="aw-field-hint">Press Enter or comma to add an email.</span>}
        </div>
      );
    }

    return (
      <div className="aw-field-group">
        <label className="aw-field-label">Value</label>
        <input
          className="aw-field-input"
          type="text"
          placeholder="Enter value"
          value={condition.value.length > 0 ? condition.value[0] : ""}
          onChange={(e) => {
            const val = e.target.value;
            updateCondition(condition.id, { value: val ? [val] : [] });
          }}
          disabled={isReadOnly}
          style={{ cursor: isReadOnly ? "default" : undefined }}
        />
      </div>
    );
  };

  const renderModal = () => {
    if (!showModal) return null;

    return (
      <div className="aw-modal-overlay aw-wrapper" onClick={closeModal}>
        <div className="aw-modal" onClick={(e) => e.stopPropagation()}>
          <div className="aw-modal-header">
            <div>
              <h5>
                <i className="fa-solid fa-chart-diagram" />
                {isReadOnly ? "Rule details" : editingWorkflow ? "Edit Rule" : "Create Rule"}
              </h5>
              {isReadOnly && (
                <div style={{ fontSize: "0.85rem", color: "#64748b", marginTop: "4px", fontWeight: "normal" }}>
                  Rule details are read-only for Sales User.
                </div>
              )}
            </div>
            <button className="aw-modal-close" onClick={closeModal} style={{ alignSelf: "flex-start" }}>
              <i className="ti ti-x" />
            </button>
          </div>

          <div className="aw-modal-body">
            <div className="aw-field-group">
              <label className="aw-field-label">Rule Name</label>
              <input
                className="aw-field-input"
                type="text"
                placeholder="e.g. Email Automation"
                value={configName}
                onChange={(e) => setConfigName(e.target.value)}
                disabled={isReadOnly}
                style={{ cursor: isReadOnly ? "default" : undefined }}
              />
            </div>

            <hr className="aw-section-divider" />

            <div className="aw-section-title">
              <i className="fa fa-filter" />
              Conditions
            </div>

            <div className="aw-rules-list">
              {conditions.length > 0 ? (
                conditions.map((condition, idx) => (
                  <React.Fragment key={condition.id}>
                    {renderConditionConnector(idx)}
                    <div className={`aw-rule-item ${expandedConditionId === condition.id ? "aw-rule-item-editing" : ""}`}>
                      {expandedConditionId === condition.id ? (
                        <div className="aw-rule-form">
                          <div className="aw-rule-form-row">
                            <div className="aw-field-group">
                              <label className="aw-field-label">Field</label>
                              <select
                                className="aw-field-select"
                                value={condition.field}
                                onChange={(e) => handleFieldChange(condition.id, e.target.value)}
                                disabled={isReadOnly}
                                style={{ cursor: isReadOnly ? "default" : undefined }}
                              >
                                {entityFields.map((f) => (
                                  <option key={f.field_name} value={f.field_name}>
                                    {f.display_name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="aw-field-group">
                              <label className="aw-field-label">Operator</label>
                              <select
                                className="aw-field-select"
                                value={condition.operator}
                                onChange={(e) =>
                                  updateCondition(condition.id, { operator: e.target.value })
                                }
                                disabled={isReadOnly}
                                style={{ cursor: isReadOnly ? "default" : undefined }}
                              >
                                {getFilteredOperators(condition.allowedOperators).map((op) => (
                                  <option
                                    key={op.approval_rule_operator_key}
                                    value={op.approval_rule_operator_key}
                                  >
                                    {op.approval_rule_operator_display_name}
                                  </option>
                                ))}
                                {condition.allowedOperators
                                  .filter(
                                    (ao) =>
                                      !operatorOptions.find(
                                        (op) =>
                                          op.approval_rule_operator_key.toUpperCase() === ao.toUpperCase()
                                      )
                                  )
                                  .map((ao) => (
                                    <option key={ao} value={ao}>
                                      {ao}
                                    </option>
                                  ))}
                                {isEmailField(condition.field) && (
                                  <option value="WILDCARD">Wildcard</option>
                                )}
                              </select>
                            </div>
                          </div>
                          {renderValueInput(condition)}
                        </div>
                      ) : (
                        <div className="aw-rule-summary">
                          <div className="aw-rule-summary-info">
                            <span className="aw-rule-num">#{idx + 1}</span>
                            <div>
                              <span className="aw-rule-text">
                                {getFieldDisplay(condition.field) || "Unconfigured"}
                              </span>
                              <span className="aw-rule-sub">
                                {getOperatorDisplay(condition.operator)}{" "}
                                {condition.value.length > 0
                                  ? `"${condition.value.join(", ")}"`
                                  : ""}
                              </span>
                            </div>
                          </div>
                          <div className="aw-rule-actions">
                            <button
                              className="aw-rule-icon-btn"
                              onClick={() => setExpandedConditionId(condition.id)}
                              title={isReadOnly ? "View Details" : "Edit"}
                            >
                              <i className={isReadOnly ? "fa fa-eye" : "fa fa-edit"} />
                            </button>
                            {!isReadOnly && (
                              <button
                                className="aw-rule-icon-btn aw-rule-icon-danger"
                                onClick={() => removeCondition(condition.id)}
                                title="Remove"
                              >
                                <i className="ti ti-trash" />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                ))
              ) : (
                <div className="aw-empty-rules">
                  <i className="ti ti-filter-off" />
                  <p>No conditions added yet</p>
                </div>
              )}

              {!isReadOnly && (
                <button className="aw-add-rule-btn" onClick={addCondition}>
                  <i className="fa fa-plus" />
                  Add Condition
                </button>
              )}
            </div>

            <hr className="aw-section-divider" />

            <div className="aw-target-entity-section">
              <div className="aw-target-entity-title">
                <i className="ti ti-database" />
                Target Entities
              </div>

              <div className="aw-entity-selector">
                <div
                  className={`aw-entity-card ${createContact ? "aw-entity-card-active" : ""} ${isReadOnly ? "aw-entity-card-readonly" : ""}`}
                  onClick={() => !isReadOnly && setCreateContact(!createContact)}
                  style={{ cursor: isReadOnly ? "default" : undefined }}
                >
                  <div className="aw-entity-card-icon">
                    <i className="ti ti-user" />
                  </div>
                  <div className="aw-entity-card-info">
                    <span className="aw-entity-card-title">Contact</span>
                    <span className="aw-entity-card-desc">Create a contact record</span>
                  </div>
                  <div className="aw-entity-checkbox">
                    <i className="fa fa-check" />
                  </div>
                </div>

                <div
                  className={`aw-entity-card ${createLead ? "aw-entity-card-active" : ""} ${isReadOnly ? "aw-entity-card-readonly" : ""}`}
                  onClick={() => !isReadOnly && setCreateLead(!createLead)}
                  style={{ cursor: isReadOnly ? "default" : undefined }}
                >
                  <div className="aw-entity-card-icon">
                    <i className="ti ti-chart-arcs" />
                  </div>
                  <div className="aw-entity-card-info">
                    <span className="aw-entity-card-title">{getLifecycleTypeName("LEAD")}</span>
                    <span className="aw-entity-card-desc">Create a new {getLifecycleTypeName("LEAD")}</span>
                  </div>
                  <div className="aw-entity-checkbox">
                    <i className="fa fa-check" />
                  </div>
                </div>
              </div>
            </div>

            <hr className="aw-section-divider" />

            <div className="aw-toggle-row">
              <div className="aw-toggle-info">
                <span className="aw-toggle-title">Enabled</span>
                <span className="aw-toggle-desc">Enable or disable this rule</span>
              </div>
              <label className="aw-switch" style={{ cursor: isReadOnly ? "default" : undefined }}>
                <input
                  type="checkbox"
                  checked={isEnabled}
                  onChange={(e) => setIsEnabled(e.target.checked)}
                  disabled={isReadOnly}
                  style={{ cursor: isReadOnly ? "default" : undefined }}
                />
                <span className="aw-switch-slider" style={{ cursor: isReadOnly ? "default" : undefined }} />
              </label>
            </div>
          </div>

          <div className="aw-modal-footer">
            <button className="aw-btn-cancel" onClick={closeModal}>
              {isReadOnly ? "Close" : "Cancel"}
            </button>
            {!isReadOnly && (
              <button className="aw-btn-save" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : editingWorkflow ? "Update Rule" : "Create Rule"}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderWorkflowCards = () => (
    <>
      <div className="aw-list-header">
        <div />
        {currentUserRole !== "PLATFORM_ANALYST" && currentUserRole !== "SALESUSER" && (
          <button className="aw-create-btn" onClick={openCreateModal}>
            <i className="fa fa-plus" />
            Create Rule
          </button>
        )}
      </div>

      {loading ? (
        <div className="aw-empty-state">
          <div className="aw-empty-icon">
            <i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} />
          </div>
          <div className="aw-empty-title">Loading...</div>
        </div>
      ) : rules.length === 0 ? (
        <div className="aw-empty-state">
          <div className="aw-empty-icon">
            <i className="fa-solid fa-chart-diagram" />
          </div>
          <div className="aw-empty-title">No Auto-Creation Rules</div>
          <p className="aw-empty-desc">
            Create your first automation rule to automatically process incoming emails
            and create contacts or leads.
          </p>
          {currentUserRole !== "PLATFORM_ANALYST" && currentUserRole !== "SALESUSER" && (
            <button className="aw-create-btn" onClick={openCreateModal}>
              <i className="fa fa-plus" />
              Create Rule
            </button>
          )}
        </div>
      ) : (
        <div className="aw-rule-grid">
          {rules.map((rule) => {
            const conditions = rule.config_json?.filter_config?.conditions || [];
            const entities = rule.config_json?.target_entity_config?.create || [];

            return (
              <div
                key={rule.crm_workflow_id}
                className={`aw-rule-card ${rule.is_enabled ? "aw-card-enabled" : "aw-card-disabled"}`}
                style={{ zIndex: openMenuId === rule.crm_workflow_id ? 100 : 1, cursor: "pointer" }}
                onClick={() => openEditModal(rule)}
              >
                <div className="aw-card-header d-flex justify-content-between align-items-center">
                  <div className="aw-card-icon">
                    <i className="fa-solid fa-chart-diagram" />
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <div className={`aw-card-type ${rule.is_enabled ? "bg-primary text-white" : "bg-secondary text-white"} px-2 rounded-pill mb-0`} style={{ textWrap: "nowrap", whiteSpace: "nowrap" }}>
                      <i className="ti ti-power text-white " style={{ fontSize: "12px" }} />
                      {rule.is_enabled ? "Enabled" : "Disabled"}
                    </div>

                    {currentUserRole !== "SALESUSER" && (
                      <div style={{ position: "relative" }}>
                        <button
                          className="aw-card-menu-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(
                              openMenuId === rule.crm_workflow_id
                                ? null
                                : rule.crm_workflow_id
                            );
                          }}
                        >
                          <i className="ti ti-dots-vertical" />
                        </button>
                        {openMenuId === rule.crm_workflow_id && (
                          <div className="aw-card-dropdown">
                            {currentUserRole !== "PLATFORM_ANALYST" &&
                              currentUserRole !== "SALESUSER" && (
                                <button
                                  className="aw-dropdown-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditModal(rule);
                                  }}
                                >
                                  <i className="fa fa-edit text-primary" />
                                  Edit
                                </button>
                              )}
                            <button
                              className="aw-dropdown-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggle(rule);
                                setOpenMenuId(null);
                              }}
                            >
                              <i className={rule.is_enabled ? "ti ti-player-pause" : "ti ti-player-play"} />
                              {rule.is_enabled ? "Disable" : "Enable"}
                            </button>
                            {currentUserRole !== "PLATFORM_ANALYST" &&
                              currentUserRole !== "SALESUSER" && (
                                <button
                                  className="aw-dropdown-item aw-dropdown-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(rule.crm_workflow_id);
                                  }}
                                >
                                  <i className="ti ti-trash text-danger" />
                                  Delete
                                </button>
                              )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div
                  className="aw-card-title aw-card-title-clickable"
                >
                  {rule.config_name}
                </div>
                <div className="aw-card-type">
                  <i className="ti ti-mail" />
                  {rule.workflow_type?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "Rule"}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  return (
    <div className="aw-wrapper">
      {renderWorkflowCards()}
      {renderModal()}
    </div>
  );
};

export default AutoCreationRules;
